/**
 * Fetch function for Jira issue → PR hydration.
 *
 * Mirrors the pattern of fetchJiraFieldsQuery in ./index.tsx:
 * - Uses fetchAgg to call AGG
 * - Wraps with inMemoryCache for deduplication
 * - Parses response into IssueLinkedPRs mapping
 */

import { type AggRequest, type AggResponse, fetchAgg } from '../../../../../common/utils/fetch-agg';
import { generateHashFromVariables } from '../../../../../utils/caching/common/key-generator';
import { inMemoryCache } from '../../../../../utils/caching/in-memory';

import {
	buildPRQueryVariables,
	jiraPRQueryGenerator,
	MAX_ISSUES_PER_QUERY,
	type SearchPageJiraPRHydrationQueryVariables,
} from './pr-query-generator';
import { type JiraPRHydrationData } from './pr-types';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { parseJiraPRResponse } from './parse-jira-pr-response';

const cache = inMemoryCache<AggResponse<JiraPRHydrationData>>();

const generateCacheKeyForJiraPRs = (issueAris: string[]): string => {
	// Sort for stable cache keys regardless of order
	const keyObj = { issueAris: [...issueAris].sort() };
	return generateHashFromVariables(keyObj);
};

// Expire cache on errors or empty response
const expireWhen = (result: AggResponse<JiraPRHydrationData>): boolean => {
	const { data, errors } = result;
	if (!data || (errors && errors.length > 0)) {
		return true;
	}
	// Check if graphStore has any data at all
	return Object.keys(data.graphStore ?? {}).length === 0;
};

export type SearchPageJiraPRHydrationQueryProps = Pick<
	AggRequest<SearchPageJiraPRHydrationQueryVariables>,
	'aggAbsoluteUrl'
> & {
	issueAris: string[];
	cloudId: string;
};

/**
 * Splits an array into chunks of the specified size.
 */
const chunk = <T,>(arr: T[], size: number): T[][] => {
	const chunks: T[][] = [];
	for (let i = 0; i < arr.length; i += size) {
		chunks.push(arr.slice(i, i + size));
	}
	return chunks;
};

/**
 * Fetches linked PR data for a single chunk of Jira issue ARIs via AGG.
 * This is the underlying fetch that handles a batch within MAX_ISSUES_PER_QUERY.
 */
const fetchJiraPRsChunk = ({
	issueAris,
	aggAbsoluteUrl,
	cloudId,
}: SearchPageJiraPRHydrationQueryProps): Promise<AggResponse<JiraPRHydrationData>> => {
	const graphQuery = jiraPRQueryGenerator(issueAris);
	const variables = buildPRQueryVariables(issueAris);

	return cache.inMemoryDecorator(
		generateCacheKeyForJiraPRs(issueAris),
		() =>
			fetchAgg<SearchPageJiraPRHydrationQueryVariables, JiraPRHydrationData>({
				variables,
				aggAbsoluteUrl,
				graphQuery,
				operationName: 'SearchPageJiraPRHydration',
				headers: {
					'X-Query-Context': `ari:cloud:platform::site/${cloudId}`,
				},
			}),
		expireWhen,
	);
};

/**
 * Merges multiple chunked AGG responses into a single response.
 * Combines all graphStore aliases and aggregates errors.
 */
const mergeChunkedResponses = (
	responses: AggResponse<JiraPRHydrationData>[],
): AggResponse<JiraPRHydrationData> => {
	const mergedGraphStore: Record<string, unknown> = {};
	const allErrors: unknown[] = [];

	responses.forEach((response, chunkIndex) => {
		if (response.data?.graphStore) {
			// Re-key aliases from each chunk to use global indices
			// Chunk responses use local aliases (issue0, issue1, ...) which we
			// remap to the global position so parseJiraPRResponse works correctly.
			const chunkOffset = chunkIndex * MAX_ISSUES_PER_QUERY;
			for (const [localKey, value] of Object.entries(response.data.graphStore)) {
				const localIndex = parseInt(localKey.replace('issue', ''), 10);
				const globalKey = `issue${chunkOffset + localIndex}`;
				mergedGraphStore[globalKey] = value;
			}
		}
		if (response.errors) {
			allErrors.push(...response.errors);
		}
	});

	return {
		data: { graphStore: mergedGraphStore },
		...(allErrors.length > 0 ? { errors: allErrors } : {}),
	} as AggResponse<JiraPRHydrationData>;
};

/**
 * Fetches linked PR data for a batch of Jira issue ARIs via AGG.
 *
 * Uses GraphQL aliases to batch issues into AGG requests. If the number of
 * issues exceeds MAX_ISSUES_PER_QUERY (100), the request is automatically
 * chunked into parallel requests and the results are merged.
 *
 * Results are cached in-memory per chunk to avoid redundant fetches.
 */
export const fetchJiraPRsQuery = async ({
	issueAris,
	aggAbsoluteUrl,
	cloudId,
}: SearchPageJiraPRHydrationQueryProps): Promise<AggResponse<JiraPRHydrationData>> => {
	if (issueAris.length <= MAX_ISSUES_PER_QUERY) {
		return fetchJiraPRsChunk({ issueAris, aggAbsoluteUrl, cloudId });
	}

	const chunks = chunk(issueAris, MAX_ISSUES_PER_QUERY);
	const responses = await Promise.all(
		chunks.map((chunkAris) => fetchJiraPRsChunk({ issueAris: chunkAris, aggAbsoluteUrl, cloudId })),
	);

	return mergeChunkedResponses(responses);
};
